import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PortalHeader } from "@/shared/layout/PortalHeader";
import { useClinicianAuthStore } from "@/stores/clinicianAuthStore";
import { getClinicianMeApi } from "@/portals/clinician/api/clinicianAuthApi";
import type { ClinicianProfile } from "@/portals/clinician/api/types";
import { ClinicianSidebar } from "./ClinicianSidebar";
import { getInitials } from "./getInitials";
import { useClinicianSearch } from "@/portals/clinician/search/useClinicianSearch";
import { clinicianName } from "@/portals/clinician/lib/clinicianName";

// Same shell every actor uses (see CaregiverLayout / AdminLayout): shared
// PortalSidebar + PortalHeader, per-route title map, routed <main>.
const pageMeta: Record<string, { title: string; description: string }> = {
  "/clinician": { title: "Review Queue", description: "Items assigned to you and waiting in the shared queue" },
  "/clinician/my-items": { title: "My Items", description: "Items you claimed or an admin assigned to you" },
};

export function ClinicianLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const signOut = useClinicianAuthStore((s) => s.signOut);
  const sessionUser = useClinicianAuthStore((s) => s.user);
  const [me, setMe] = useState<ClinicianProfile | null>(null);
  const meta = location.pathname.startsWith("/clinician/review/") || location.pathname.startsWith("/clinician/my-items/")
    ? { title: "Review Item", description: "Records, RxNorm matches and the patient's answers" }
    : pageMeta[location.pathname] ?? pageMeta["/clinician"];

  useEffect(() => {
    let cancelled = false;
    getClinicianMeApi().then(({ data }) => {
      if (!cancelled) setMe(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // /me is the source of truth for the name; the login response's user (if the
  // backend sends one) only covers the moment before it resolves or if it fails.
  const profile = me ?? sessionUser;
  const name = profile ? clinicianName(profile) : "";

  const handleLogout = () => {
    void signOut();
    navigate("/clinician/login", { replace: true });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[#F7F7F7]">
      <ClinicianSidebar name={name} />
      <div className="flex flex-1 flex-col overflow-hidden">
        <PortalHeader
          title={meta.title}
          description={meta.description}
          avatarInitials={getInitials(name)}
          showNotifications
          notificationsVariant="clinician"
          useSearchSources={useClinicianSearch}
          menuItems={[{ label: "Logout", onClick: handleLogout, danger: true }]}
        />
        <main id="main-content" className="msq-route-main flex-1 overflow-y-auto bg-[#F9F9F9]">
          {children}
        </main>
      </div>
    </div>
  );
}
